import {
  WSActions,
  WSElement,
  WSFlexBox,
  WSPill,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import {
  IWorkItem,
  WorkLogStatus,
  WSDataTypes
} from "@wingspanhq/payments/dist/interfaces";
import { IWorkDefinitionResponse } from "@wingspanhq/payments/dist/interfaces/api/workDefinition";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { useMemo } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import { formatter } from "../../../../shared/utils/formatter";
import {
  getWorkItemEditorName,
  isEditedAfterLastSubmission
} from "../../utils/workLogPermissions";
import { AcceptedValuesResources } from "../FormPartialAttributes/helpers";
import { useModalResourceDrillDownHistory } from "../../../../shared/components/ResourceHistory/ModalResourceDrillDownHistory";
import { useModalEditItem } from "./ModalEditItem";
import { useModalEditItemPayer } from "./ModalEditItemPayer";
import { useModalRemoveItem } from "./ModalRemoveItem";
import { useModalRemoveItemPayer } from "./ModalRemoveItemPayer";
import { getLatestWorkItemComment } from "../../utils/getLatestWorkItemComment";
import styles from "./Item.module.scss";

type Props = {
  noEdit?: boolean;
  payerEdit?: boolean;
  showViewHistory?: boolean;
  contractorName?: string;
  workItem: IWorkItem;
  workDefinitions: IWorkDefinitionResponse[];
  resources: AcceptedValuesResources;
  currentUserRole?: "payer" | "payee";
  otherPartyName?: string;
  workLogStatus?: WorkLogStatus | string;
  lastSubmittedAt?: Date | string;
};

const getFormulaString = (
  workItem: IWorkItem,
  workDefinition: IWorkDefinitionResponse
) => {
  const calculation = workItem.calculations?.items.find(
    calc => calc.result === workItem.calculations?.result
  );

  if (!calculation) {
    return "";
  }

  const definitions = workDefinition.attributeDefinitions;

  const getInterpolatedFormula = () => {
    // convert format: "${workItem.hours} * 100" to "10 hours * 100" by definitions;
    return calculation.formula.replace(/\${(.*?)\}/g, (match, key) => {
      const field = key.split(".")[1];

      const definition = definitions.find(
        definition => definition.key === field
      );

      if (!definition) {
        return match;
      }

      if (!workItem.attributes[field]) {
        return "";
      }

      const definitionName = definition.name.toLowerCase();
      const value = workItem.attributes[field];

      switch (definition.type) {
        case WSDataTypes.Boolean:
          return `${value ? "Yes" : "No"} ${definitionName}`;
        case WSDataTypes.Datetime:
          return `${formatter.format(value, {
            dateType: "monthDayYear"
          })} ${definitionName}`;
        case WSDataTypes.Number:
        case WSDataTypes.String:
        default:
          return `${value} ${definitionName}`;
      }
    });
  };

  /*
  try {
    const interpolatedFormula = getInterpolatedFormula();
    if (interpolatedFormula) {
      return interpolatedFormula;
    }
  } catch (error) {
    console.error("Error interpolating formula:", error);
  }
  */

  return calculation.formula;
};

export const Item: React.FC<Props> = props => {
  const userId = useUserId();
  const workDefinition = props.workDefinitions.find(
    workDefinition =>
      workDefinition.workDefinitionId === props.workItem.workDefinitionId
  );

  const attributeDescriptions = useMemo(() => {
    if (!workDefinition) {
      return [];
    }

    return Object.entries(props.workItem.attributes).map(([key, value]) => {
      const attribute = workDefinition.attributeDefinitions.find(
        attribute => attribute.key === key
      );

      if (!attribute) {
        return "";
      }

      return `${attribute.name}: ${formatter.format(value, {
        dateType: "monthDayYear"
      })}`;
    });
  }, [props.workItem, workDefinition]);

  const modalEditItem = useModalEditItem();
  const modalRemoveItem = useModalRemoveItem();
  const modalEditItemPayer = useModalEditItemPayer();
  const modalRemoveItemPayer = useModalRemoveItemPayer();
  const modalItemHistory = useModalResourceDrillDownHistory();

  const editorName = useMemo(
    () =>
      getWorkItemEditorName(
        props.workItem as any,
        props.currentUserRole,
        props.otherPartyName
      ),
    [props.workItem, props.currentUserRole, props.otherPartyName]
  );

  const showEditedChip =
    props.workLogStatus !== WorkLogStatus.Draft &&
    isEditedAfterLastSubmission(
      props.workItem.updatedAt,
      props.lastSubmittedAt
    );

  const isEditorPayee = useMemo(() => {
    if (!props.currentUserRole || !editorName) {
      return false;
    }

    // When viewing as a payer, if editor name matches the other party (contractor/payee)
    if (props.currentUserRole === "payer") {
      return editorName === props.otherPartyName;
    }

    // When viewing as a payee, if editor name doesn't match the other party (payer)
    // then it must be the payee (self)
    if (props.currentUserRole === "payee") {
      return editorName !== props.otherPartyName;
    }

    return false;
  }, [props.currentUserRole, editorName, props.otherPartyName]);

  if (!workDefinition) {
    return null;
  }

  const formulaString = getFormulaString(props.workItem, workDefinition);

  return (
    <WSElement>
      <WSFlexBox justify="space-between" wrap="nowrap" alignItems="center">
        <WSElement className={styles.summary}>
          <WSFlexBox.CenterY wrap="wrap" gap="S" mb="XS">
            <WSText weight="medium">{workDefinition?.name}</WSText>
            {showEditedChip && editorName && (
              <WSPill
                theme={isEditorPayee ? "warning" : "violet"}
                type="light"
                text={`Last edited by ${editorName}`}
              />
            )}
          </WSFlexBox.CenterY>
          {formulaString && (
            <WSText.ParagraphXs
              color="gray400"
              mb="XS"
              className={styles.formula}
            >
              {formulaString}
            </WSText.ParagraphXs>
          )}
          {attributeDescriptions.map(description => (
            <WSText.ParagraphXs key={description} color="gray400">
              {description}
            </WSText.ParagraphXs>
          ))}
        </WSElement>

        <WSElement>
          <WSText.ParagraphSm mb="L" align="right">
            {formatMoney(props.workItem.calculations?.result || 0, "USD")}
          </WSText.ParagraphSm>

          {showEditedChip && (
            <WSFlexBox justify="flex-end" mb="M">
              <WSPill theme="violet" type="light" text="Edited" />
            </WSFlexBox>
          )}

          {!props.noEdit || props.payerEdit ? (
            <WSActions
              buttons={[
                {
                  kind: "Link",
                  label: "Edit",
                  onClick: () => {
                    if (props.payerEdit) {
                      modalEditItemPayer.open({
                        workItem: props.workItem,
                        workDefinition: workDefinition!,
                        resources: props.resources,
                        contractorName: props.contractorName
                      });
                    } else {
                      modalEditItem.open({
                        workItem: props.workItem,
                        workDefinition: workDefinition!,
                        resources: props.resources,
                        clientName: props.otherPartyName
                      });
                    }
                  }
                }
              ]}
              menuItems={[
                {
                  icon: "time",
                  label: "View item history",
                  onClick: () => {
                    modalItemHistory.open({
                      resource: {
                        resourceType: "WorkItem",
                        resourceId: props.workItem.workItemId
                      },
                      title: workDefinition?.name
                        ? `Item history: ${workDefinition.name}`
                        : "Item history"
                    });
                  }
                } as const,
                {
                  icon: "trash",
                  iconProps: {
                    color: "red400"
                  },
                  label: "Remove item",
                  onClick: () => {
                    if (props.payerEdit) {
                      modalRemoveItemPayer.open({
                        workItem: props.workItem,
                        workDefinition: workDefinition!,
                        resources: props.resources,
                        contractorName: props.contractorName
                      });
                    } else {
                      modalRemoveItem.open({
                        workItemId: props.workItem.workItemId,
                        name: workDefinition?.name || ""
                      });
                    }
                  }
                }
              ]}
            />
          ) : props.showViewHistory ? (
            <WSActions
              menuItems={[
                {
                  icon: "time",
                  label: "View item history",
                  onClick: () => {
                    modalItemHistory.open({
                      resource: {
                        resourceType: "WorkItem",
                        resourceId: props.workItem.workItemId
                      },
                      title: workDefinition?.name
                        ? `Item history: ${workDefinition.name}`
                        : "Item history"
                    });
                  }
                }
              ]}
            />
          ) : null}
        </WSElement>
      </WSFlexBox>

      <WorkItemComments
        workItem={props.workItem}
        currentUserRole={props.currentUserRole}
        otherPartyName={props.otherPartyName}
      />
    </WSElement>
  );
};

const InlineComment: React.FC<{
  label: string;
  comment: string;
  date?: Date | string;
}> = ({ label, comment, date }) => (
  <WSElement colorBackground="gray50" p="M" style={{ borderRadius: 2 }}>
    <WSFlexBox.CenterY wrap="wrap" gap="S">
      <WSText.ParagraphXs color="gray500">{label}</WSText.ParagraphXs>
      {date && (
        <>
          <WSElement
            colorBackground="gray400"
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              flexShrink: 0
            }}
          />
          <WSText.ParagraphXs color="gray500">
            {toWSDateString(date, "monthDayYear")}
          </WSText.ParagraphXs>
        </>
      )}
    </WSFlexBox.CenterY>
    <WSText.ParagraphXs color="gray600" py="S">
      &ldquo;{comment}&rdquo;
    </WSText.ParagraphXs>
  </WSElement>
);

const WorkItemComments: React.FC<{
  workItem: IWorkItem;
  currentUserRole?: "payer" | "payee";
  otherPartyName?: string;
}> = ({ workItem, currentUserRole, otherPartyName }) => {
  const latest = getLatestWorkItemComment(
    (workItem as any)?.payerOwnedData,
    (workItem as any)?.payeeOwnedData
  );

  if (!latest) {
    return null;
  }

  const label =
    latest.source === "payer"
      ? currentUserRole === "payer"
        ? "Your comment"
        : `Comment from ${otherPartyName || "client"}`
      : currentUserRole === "payee"
        ? "Your comment"
        : `Comment from ${otherPartyName || "contractor"}`;

  return (
    <WSElement mt="M">
      <InlineComment
        label={label}
        comment={latest.comment}
        date={latest.commentAt}
      />
    </WSElement>
  );
};
